import React from "react";
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";

type CommonStatusRendererArgs = {
    statusValue: any;
    tabKey: string;
    controllerName: string;
    onRefreshController?: (e: React.MouseEvent, controllerName: string) => void;
    renderOnOffHighlighted?: (text: string) => React.ReactNode;
    refreshButtonExtraClassName?: string;
    onRefreshClickWrapper?: (
        e: React.MouseEvent<HTMLButtonElement>,
        handler: (ev: React.MouseEvent<HTMLButtonElement>) => void
    ) => void;
};

export const renderStatusCellCommon = ({
    statusValue,
    tabKey,
    controllerName,
    onRefreshController,
    renderOnOffHighlighted,
    refreshButtonExtraClassName,
    onRefreshClickWrapper,
}: CommonStatusRendererArgs) => {
    const statusLower = String(statusValue ?? "").toLowerCase().trim();
    const trimmedStatus = String(statusValue ?? "").trim();
    const isNoDataStatus = trimmedStatus === "-";
    const isNotReachable =
        /(^|\s)not\s+reachable(\s|$)/.test(statusLower) ||
        /(^|\s)off(line)?(\s|$)/.test(statusLower);
    const isReachable =
        !isNotReachable &&
        (/(^|\s)reachable(\s|$)/.test(statusLower) ||
            /(^|\s)on(line)?(\s|$)/.test(statusLower));
    const circleClass = isReachable ? "online" : "offline";

    const isControllersTab = tabKey === "controllers";

    const handleRefreshClick = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        if (!onRefreshController) return;
        const handler = (ev: React.MouseEvent<HTMLButtonElement>) =>
            onRefreshController(ev, controllerName);
        if (onRefreshClickWrapper) {
            onRefreshClickWrapper(e as React.MouseEvent<HTMLButtonElement>, handler);
        } else {
            onRefreshController(e as React.MouseEvent<HTMLButtonElement>, controllerName);
        }
    };

    if (isNoDataStatus) {
        return (
            <span style={{ display: "flex", alignItems: "center" }}>
                <span style={{ marginLeft: 8 }}>-</span>
                {isControllersTab && onRefreshController && (
                    <IconButton
                        disableRipple
                        className={`mr-2 bigRefresh${
                            refreshButtonExtraClassName
                                ? ` ${refreshButtonExtraClassName}`
                                : ""
                        }`}
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            marginLeft: 8,
                        }}
                        aria-label="Refresh controller"
                        onClick={handleRefreshClick}
                        onKeyDown={handleRefreshClick}
                    >
                        <img
                            src={refreshIcon}
                            alt="refresh controller"
                        />
                    </IconButton>
                )}
            </span>
        );
    }

    return (
        <span style={{ display: "flex", alignItems: "center" }}>
            <i
                className={`material-icons circle ${circleClass}`}
                style={{ marginRight: 6 }}
            >
                fiber_manual_record
            </i>
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                }}
            >
                {isControllersTab && renderOnOffHighlighted
                    ? renderOnOffHighlighted(statusValue)
                    : statusValue}
            </span>
            {isControllersTab && onRefreshController && (
                <IconButton
                    disableRipple
                    className={`mr-2 bigRefresh${
                        refreshButtonExtraClassName
                            ? ` ${refreshButtonExtraClassName}`
                            : ""
                    }`}
                    style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        marginLeft: 8,
                    }}
                    aria-label="Refresh controller"
                    onClick={handleRefreshClick}
                    onKeyDown={handleRefreshClick}
                >
                    <img
                        src={refreshIcon}
                        alt="refresh controller"
                    />
                </IconButton>
            )}
        </span>
    );
};

type CommonLastActiveRendererArgs = {
    row: any;
    col: string;
    tabKey: string;
    onRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        row: any
    ) => void | Promise<void>;
    refreshAltText?: string;
};

export const renderLastActiveCellCommon = ({
    row,
    col,
    tabKey,
    onRefreshSensor,
    refreshAltText = "refresh",
}: CommonLastActiveRendererArgs) => (
    <span style={{ display: "flex", alignItems: "center" }}>
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
            }}
        >
            {row[col]}
        </span>
        {tabKey === "sensors" && (
            <IconButton
                disableRipple
                className="mr-2 bigRefresh refresh-sensor"
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    marginLeft: 8,
                }}
                aria-label="Refresh sensor reading"
                data-sensor-id={`${row.ldsu_uuid}-${row.SAID}`}
                onClick={(e) => {
                    e.stopPropagation();
                    onRefreshSensor(e, row);
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    onRefreshSensor(e, row);
                }}
            >
                <img
                    src={refreshIcon}
                    alt={refreshAltText}
                />
            </IconButton>
        )}
    </span>
);


